import { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import { useSkin } from '@/store/SkinContext';
import { SKIN_ICON } from '@/components/AppShell';
import { CARD_DIMS, type Formato, type PosV, type PosH } from '@/components/AtletaCard';
import type { PodioRunner } from '@/lib/api';
import { pillColor } from '@/lib/podios/pillColors';

/** #rgb / #rrggbb → rgba(r,g,b,a). Para el stop transparente del scrim (mismo
 *  color con alpha) y evitar el fade a negro de html2canvas. */
function hexToRgba(hex: string, alpha: number): string {
  let h = hex.trim().replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return `rgba(0,0,0,${alpha})`;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Abrevia el nombre de una disciplina de combinada para las cajas del
 *  desglose (espacio muy justo con 7-10 pruebas). Description ya viene corto
 *  ("Altura", "Peso", "200 m", "100 m vallas"); solo comprimimos lo largo. */
function abrevPrueba(n: string): string {
  // Enfoque por PALABRA CLAVE: la fuente mete de todo en el nombre de la
  // disciplina (nombre completo del sub-evento, género, tipo de combinada y el
  // spec del artefacto: "Peso 6 kg", "110 m Vallas 0,991"…). En vez de intentar
  // quitar cada añadido, detectamos la disciplina y devolvemos su nombre corto
  // canónico, ignorando el resto (Riki 2026-07-21).
  const s = (n || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  // Concursos (por palabra clave; se ignoran pesos/alturas/etc.).
  if (/longitud/.test(s)) return 'Long.';
  if (/altura/.test(s)) return 'Altura';
  if (/pertiga/.test(s)) return 'Pért.';
  if (/triple/.test(s)) return 'Triple';
  if (/peso/.test(s)) return 'Peso';
  if (/disco/.test(s)) return 'Disco';
  if (/martillo/.test(s)) return 'Mart.';
  if (/jabalina/.test(s)) return 'Jabal.';
  // Carreras: la distancia es el PRIMER número del nombre (el resto —altura de
  // vallas, categoría…— se descarta).
  const dist = (n.match(/(\d[\d.]*)/) || [])[1] || '';
  if (/vallas/.test(s)) return dist ? `${dist}mv` : 'Vallas';
  if (/obstaculos|steeple/.test(s)) return dist ? `${dist} obst` : 'Obst.';
  if (dist) return dist;
  return (n || '').trim();
}

interface Props {
  competicion: string;
  categoria: string;
  fecha?: string;
  /** Protagonista + vecinos (1-3), YA en orden de puesto. */
  filas: PodioRunner[];
  /** Índice del protagonista dentro de `filas` (0, 1 o 2). */
  protagonistaEnFila: number;
  formato: Formato;
  escala: number;
  degradado: number;
  opacidad: number;
  posicion: PosV;
  posH: PosH;
  branding: PosV;
  fotoSrc?: string;
  /** Color del scrim (fondo del bloque de datos). Si se omite, usa el de la
   *  skin (`var(--ld-bg)`). Permite forzar contraste sobre fotos claras. */
  scrimColor?: string;
  /** Modo overlay para el vídeo: fondo transparente, sin foto. */
  overlay?: boolean;
  overlayPart?: 'datos' | 'logo';
}

/** Ficha "protagonista + vecinos" para la página pública de atleta. Hermana de
 *  PodioCard (misma skin, mismo auto-ajuste y scrim), pero en vez del podio 1-2-3
 *  destaca al atleta seleccionado EN GRANDE y muestra al de arriba y al de abajo
 *  como contexto (puestos reales, no 1/2/3). Se rasteriza con html2canvas. */
export const FichaVivoCard = forwardRef<HTMLDivElement, Props>(function FichaVivoCard(
  { competicion, categoria, fecha, filas, protagonistaEnFila, formato, escala, degradado, opacidad, posicion, posH, branding, fotoSrc, scrimColor, overlay = false, overlayPart },
  ref,
) {
  const { skin } = useSkin();
  const { w, h } = CARD_DIMS[formato];

  // Auto-ajuste: el bloque de datos nunca se sale de la tarjeta (mismo criterio
  // que PodioCard). Tras cada render se mide y, si sobresale, se reduce la
  // escala efectiva con reflow real.
  const bloqueRef = useRef<HTMLDivElement | null>(null);
  const [ajuste, setAjuste] = useState(1);
  useLayoutEffect(() => {
    setAjuste(1);
  }, [escala, formato, posicion, posH, branding, filas, competicion, categoria, fecha, protagonistaEnFila]);
  useLayoutEffect(() => {
    const el = bloqueRef.current;
    if (!el) return;
    if (el.offsetHeight > h && ajuste > 0.35) {
      const objetivo = Math.max(0.35, (h / el.offsetHeight) * ajuste * 0.98);
      if (objetivo < ajuste - 0.005) setAjuste(objetivo);
    }
  });

  const fs = (px: number) => Math.round(px * escala * ajuste);
  const mutedOp = 0.7;

  const arriba = posicion === 'arriba';
  const ancho169 = formato === '16:9';
  const izq = posH === 'izquierda';
  const showData = !overlay || overlayPart !== 'logo';
  // Sin foto la tarjeta se vuelve un "marco": fondo transparente + scrim + texto,
  // para descargarlo y superponerlo sobre otra imagen o vídeo. Por eso el scrim
  // se muestra tanto con foto (contraste sobre la foto) como sin ella (el marco).
  const showScrim = overlay ? showData : true;
  // Halo de legibilidad: un text-shadow del color del scrim que abraza cada
  // glifo. Da contraste SIN tapar la foto (a diferencia de subir el scrim).
  // html2canvas sí rasteriza text-shadow (backdrop-filter/blur no → sin blur).
  // Intensidad contenida a propósito (Riki 2026-07-12: más se veía feo). Solo
  // con foto/scrim y color en hex; blur escalado con fs() para consistencia.
  const halo = showScrim && scrimColor
    ? `0 0 ${fs(3)}px ${hexToRgba(scrimColor, 0.7)}, 0 0 ${fs(8)}px ${hexToRgba(scrimColor, 0.4)}`
    : undefined;
  // Se aplica SOLO al texto que cae en la zona donde el gradiente ya es ~0 (el
  // extremo opuesto al borde anclado), no a todo (Riki 2026-07-12):
  //  - bloque abajo  → transparente ARRIBA → halo en la cabecera (título).
  //  - bloque arriba → transparente ABAJO  → halo en la última fila + branding.
  const haloTitulo = halo && !arriba ? halo : undefined;
  const haloUltimaFila = halo && arriba ? halo : undefined;
  // El branding solo lleva halo si queda en el extremo transparente.
  const haloBrand =
    halo && ((branding === 'arriba' && !arriba) || (branding === 'abajo' && arriba)) ? halo : undefined;

  const brandRow = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontFamily: 'var(--ld-font-mono)', fontSize: fs(20), letterSpacing: '0.18em', color: 'var(--ld-text)', textShadow: haloBrand }}>
      <span style={{ opacity: mutedOp }}>mundoatletismo.es</span>
      <span style={{ flex: 1, height: 2, background: 'var(--ld-line-strong, var(--ld-line))' }} />
      <img src={SKIN_ICON[skin]} alt="" width={fs(36)} height={fs(36)} style={{ display: 'block' }} />
    </div>
  );

  const cabecera = (
    <div style={{ textShadow: haloTitulo }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ fontFamily: 'var(--ld-font-display)', fontWeight: 800, fontSize: fs(40), lineHeight: 1.05, textTransform: 'uppercase', letterSpacing: '0.01em', flex: 1 }}>
          {competicion}
        </div>
        {fecha ? (
          <div style={{ fontFamily: 'var(--ld-font-mono)', fontSize: fs(18), letterSpacing: '0.08em', color: 'var(--ld-text)', opacity: mutedOp, border: '2px solid var(--ld-line-strong, var(--ld-line))', borderRadius: 999, padding: `${fs(6)}px ${fs(14)}px`, whiteSpace: 'nowrap' }}>
            {fecha}
          </div>
        ) : null}
      </div>
      <div style={{ fontFamily: 'var(--ld-font-mono)', fontWeight: 700, fontSize: fs(20), letterSpacing: '0.12em', color: 'var(--ld-accent)', textTransform: 'uppercase', marginTop: fs(6) }}>
        {categoria}
      </div>
    </div>
  );

  const bloqueAtletas = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: fs(14) }}>
      {filas.map((p, i) => {
        const yo = i === protagonistaEnFila;
        // Protagonista: tipografía grande y color de acento. Vecinos: SOLO
        // menores (misma jerarquía por tamaño), con color normal — nada de gris
        // ni opacidad reducida, que bajo el velo del scrim se volvían ilegibles
        // (números 1/3, Riki 2026-07-11). Puestos reales (no 1/2/3).
        const colPuesto = yo ? 'var(--ld-accent)' : 'var(--ld-text)';
        const fsPuesto = yo ? fs(58) : fs(34);
        const fsNombre = yo ? fs(38) : fs(24);
        const fsMarca = yo ? fs(50) : fs(30);
        // Relevos: bajo el club (misma línea) pintamos los relevistas
        // "P. Rojo - H. Alvarez - …" (inicial del nombre + primer apellido) en
        // vez del club, que ya es el nombre principal (Riki 2026-07-21).
        const relevistas = p.integrantes?.length
          ? p.integrantes
              .map((m) => {
                const ini = (m.nombre || '').trim().charAt(0);
                const ape = (m.apellidos || '').trim().split(/\s+/)[0] || '';
                return ini ? `${ini}. ${ape}` : ape;
              })
              .filter(Boolean)
              .join(' - ')
          : null;
        // UNA sola fila: puesto | columna izquierda (nombre → club →
        // etiquetas → intentos, APILADOS arriba sin huecos — bug del hueco
        // visto por Riki 2026-07-11: club/etiquetas vivían fuera de la fila
        // y la columna marca+viento la estiraba) | columna derecha
        // (marca+viento, alineados por la derecha entre ellos) | Q/q.
        return (
          <div key={`${p.puesto}-${p.nombre}`} style={{ display: 'flex', alignItems: 'flex-start', gap: fs(16), textShadow: i === filas.length - 1 ? haloUltimaFila : undefined }}>
            <span style={{ fontFamily: 'var(--ld-font-display)', fontWeight: 800, fontSize: fsPuesto, lineHeight: 1, color: colPuesto, minWidth: fs(56) }}>
              {p.puesto}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: fs(4), flex: 1, minWidth: 0 }}>
              <span style={{ fontFamily: 'var(--ld-font-display)', fontWeight: yo ? 800 : 700, fontSize: fsNombre, lineHeight: 1.05, textTransform: 'uppercase' }}>
                {p.nombre}
              </span>
              {/* Club + píldoras en la MISMA fila (Riki 2026-07-14): las
                  etiquetas ya no ocupan un renglón propio → el bloque de texto
                  es menos alto. Club en NEGRITA (antes se veía muy fino). */}
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: fs(8), marginTop: fs(2) }}>
                <span style={{ fontFamily: 'var(--ld-font-mono)', fontWeight: 700, fontSize: fs(17), letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ld-text)', opacity: mutedOp }}>
                  {relevistas || p.club_real || p.club}
                </span>
                {p.etiquetas?.map((tag) => (
                  <span key={tag} style={{ fontFamily: 'var(--ld-font-mono)', fontSize: fs(14), fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', background: pillColor(tag).bg, color: pillColor(tag).fg, borderRadius: 999, padding: `${fs(3)}px ${fs(10)}px` }}>
                    {tag}
                  </span>
                ))}
              </div>
              {/* Serie de intentos SOLO del protagonista (concursos, rfea.es).
                  Los vecinos se quedan en su mejor marca (regla de Riki). Dos
                  modos: LISTONES (altura/pértiga → últimas 3 alturas
                  intentadas, con sus símbolos O/XO/XXX/r) y SERIE (long/
                  triple/lanzamientos → distancias, "X" nulo). */}
              {yo && p.intentos?.length ? (() => {
                const esListones = p.intentos!.some((a) => !!a.height);
                const serie = esListones
                  ? p.intentos!.filter((a) => /[xor]/i.test(a.mark)).slice(-3)
                  : p.intentos!;
                return (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: fs(6), marginTop: fs(4) }}>
                    {serie.map((a, k) => {
                      const fallo = /^x+$/i.test(a.mark) || /^r$/i.test(a.mark) || a.mark === '' || a.mark === '-';
                      const best = esListones ? !!a.height && a.height === p.marca : !!a.mark && a.mark === p.marca;
                      return (
                        <span
                          key={k}
                          style={{
                            display: 'inline-flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            fontFamily: 'var(--ld-font-mono)',
                            fontWeight: 700,
                            minWidth: fs(46),
                            padding: `${fs(3)}px ${fs(8)}px`,
                            borderRadius: fs(6),
                            border: `2px solid ${best ? 'var(--ld-accent)' : 'var(--ld-line-strong, var(--ld-line))'}`,
                            color: fallo && !best ? 'var(--ld-text-muted)' : best ? 'var(--ld-accent)' : 'var(--ld-text)',
                            opacity: fallo && !best ? mutedOp : 1,
                          }}
                        >
                          {esListones ? (
                            <>
                              <span style={{ fontSize: fs(16) }}>{a.height}</span>
                              <span style={{ fontSize: fs(13), letterSpacing: '0.08em' }}>{a.mark || '–'}</span>
                            </>
                          ) : (
                            <span style={{ fontSize: fs(18) }}>{a.mark || '–'}</span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                );
              })() : null}
              {/* Desglose de PRUEBA COMBINADA (hept/decatlón) — SOLO el
                  protagonista. Cajas compactas que hacen wrap (7 en chicas, 10
                  en chicos): abreviatura de la prueba + marca + puntos de esa
                  prueba. El auto-ajuste del bloque encoge si no cabe. */}
              {yo && p.combinada?.length ? (() => {
                // TODAS las cajas en UNA sola fila (decatlón = 10 incluido, Riki
                // 2026-07-14), CENTRADAS respecto al ancho de la tarjeta y con un
                // margen lateral simétrico (sm), sin pegar a los bordes. Puede ser
                // más ancho que la columna del nombre porque a esta altura, bajo el
                // puesto y la marca, no hay nada que pisar (el bloque se centra con
                // marginLeft = sm - inicioColumna → márgenes iguales a ambos lados).
                const contentW = w - 112;      // ancho útil (padding 56 a cada lado)
                const sm = fs(8);              // margen lateral simétrico (estrecho:
                                               // aprovecha el hueco para ensanchar las
                                               // cajas sin tocar el gap, Riki 2026-07-21)
                const extra = fs(56) + fs(16); // inicio de la columna central (puesto + gap)
                return (
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${p.combinada.length}, minmax(0, 1fr))`, gap: fs(8), marginTop: fs(6), marginLeft: sm - extra, width: contentW - 2 * sm }}>
                  {p.combinada.map((d, k) => (
                    <span
                      key={k}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: fs(1),
                        minWidth: 0, padding: `${fs(4)}px ${fs(5)}px`, borderRadius: fs(6),
                        border: `2px solid var(--ld-line-strong, var(--ld-line))`,
                      }}
                    >
                      <span style={{ fontFamily: 'var(--ld-font-mono)', fontSize: fs(12), letterSpacing: '0.02em', textTransform: 'uppercase', opacity: mutedOp, whiteSpace: 'nowrap' }}>
                        {abrevPrueba(d.disciplina)}
                      </span>
                      {/* Solo prueba + marca; la puntuación IAAF PARCIAL de cada
                          disciplina se omite (Riki 2026-07-14): solo interesa la
                          puntuación FINAL del hept/decatlón (la marca del atleta). */}
                      <span style={{ fontFamily: 'var(--ld-font-display)', fontWeight: 800, fontSize: fs(18), lineHeight: 1, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {d.marca}
                      </span>
                    </span>
                  ))}
                </div>
                );
              })() : null}
            </div>
            {/* Columna derecha: marca y, SIEMPRE debajo, el viento (alineados
                por la derecha entre ellos). La Q/q fuera, a tamaño de marca. */}
            <span style={{ display: 'flex', alignItems: 'flex-start', gap: fs(16) }}>
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: fs(4) }}>
                <span style={{ fontFamily: 'var(--ld-font-display)', fontWeight: 800, fontSize: fsMarca, lineHeight: 1, color: colPuesto, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  {p.marca}
                </span>
                {p.viento ? (
                  <span style={{ fontFamily: 'var(--ld-font-mono)', fontSize: fs(16), color: 'var(--ld-text)', opacity: mutedOp, whiteSpace: 'nowrap' }}>{p.viento}</span>
                ) : null}
              </span>
              {p.quali ? (
                <span style={{ fontSize: fsMarca, lineHeight: 1, fontFamily: 'var(--ld-font-mono)', fontWeight: 700, color: 'var(--ld-accent)' }}>
                  {p.quali}
                </span>
              ) : null}
            </span>
          </div>
        );
      })}
    </div>
  );

  // Color del velo con alpha variable. El stop transparente debe ser el MISMO
  // color con alpha 0 (no la palabra `transparent` = negro-alpha0): html2canvas
  // no premultiplica al interpolar y derivaría a un tinte oscuro. Con scrimColor
  // (hex) modulamos la opacidad por tramos; sin él, caemos al color de skin.
  const scrimRGBA = (a: number) =>
    scrimColor ? hexToRgba(scrimColor, a) : a <= 0.001 ? 'transparent' : 'var(--ld-bg)';
  // Perfil del velo (Riki 2026-07-21): 100% de opacidad hasta el 18% de su
  // altura y luego caída NO lineal —lenta al principio, rápida al final— para
  // seguir potente más tiempo. Los % son fracciones del recorrido total del
  // velo, así que el punto donde llega a 0% NO cambia (sigue en degradado%).
  const D = degradado;
  const scrimGrad = [
    `${scrimRGBA(1)} 0%`,
    `${scrimRGBA(1)} ${Math.round(0.18 * D)}%`,
    `${scrimRGBA(0.92)} ${Math.round(0.45 * D)}%`,
    `${scrimRGBA(0.68)} ${Math.round(0.70 * D)}%`,
    `${scrimRGBA(0.32)} ${Math.round(0.88 * D)}%`,
    `${scrimRGBA(0)} ${D}%`,
  ].join(', ');
  const scrimStyle = showScrim
    ? {
        position: 'absolute' as const,
        // Sangrado vertical de unos px: el borde anclado (arriba/abajo) es el
        // lado OPACO; sin este bleed, el rasterizado de html2canvas puede dejar
        // 1-2 px sin cubrir en el filo. Solo vertical, para no invadir la foto
        // por los lados en 16:9 (Riki 2026-07-21).
        top: -6,
        bottom: -6,
        left: 0,
        right: 0,
        background: `linear-gradient(${arriba ? 'to bottom' : 'to top'}, ${scrimGrad})`,
        opacity: opacidad / 100,
        zIndex: 0,
      }
    : null;

  return (
    <div
      ref={ref}
      style={{
        width: w,
        height: h,
        position: 'relative',
        overflow: 'hidden',
        // Con foto: base sólida bajo la imagen. Sin foto (o en overlay): transparente,
        // para que html2canvas (backgroundColor:null) exporte el marco con alfa.
        background: fotoSrc && !overlay ? 'var(--ld-bg)' : 'transparent',
        color: 'var(--ld-text)',
        fontFamily: 'var(--ld-font-sans)',
      }}
    >
      {fotoSrc && !overlay ? (
        <img src={fotoSrc} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : null}

      {showData ? (
        <div
          ref={bloqueRef}
          style={{
            position: 'absolute',
            left: ancho169 ? (izq ? 0 : undefined) : 0,
            right: ancho169 ? (izq ? undefined : 0) : 0,
            maxWidth: ancho169 ? '50%' : undefined,
            // El bloque SOBRESALE 8 px por el borde anclado (arriba/abajo), que
            // es el lado opaco del velo; el overflow:hidden de la tarjeta lo
            // recorta. Así el velo cubre hasta el último píxel aunque html2canvas
            // recorte el degradado al bloque (dejaba una línea de 2-3 px, Riki
            // 2026-07-21). El padding de ese lado se compensa (+8) para que el
            // contenido NO se mueva.
            top: arriba ? -8 : undefined,
            bottom: arriba ? undefined : -8,
            paddingLeft: 56,
            paddingRight: 56,
            paddingTop: arriba ? 64 : 56,
            paddingBottom: arriba ? 56 : 64,
          }}
        >
          {scrimStyle ? <div style={scrimStyle} /> : null}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', gap: fs(14) }}>
            {branding === 'arriba' ? brandRow : null}
            {cabecera}
            {bloqueAtletas}
            {branding === 'abajo' ? brandRow : null}
          </div>
        </div>
      ) : null}
    </div>
  );
});
